import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, TrendingUp, Plus, DollarSign } from 'lucide-react';
import { toast } from 'sonner';
import Navbar from '@/components/Navbar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function InvestmentPage() {
  const [investments, setInvestments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadInvestments();
  }, []);

  const loadInvestments = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/investments`, {
        withCredentials: true
      });
      setInvestments(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Load investments error:', error);
      setLoading(false);
    }
  };

  const totalInvested = investments.reduce((sum, inv) => sum + inv.amount, 0);
  const totalEarnings = investments.reduce((sum, inv) => sum + inv.earnings, 0);

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50">
        <Navbar />
        <div className="flex items-center justify-center h-[80vh]">
          <Loader2 className="w-12 h-12 text-indigo-600 animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50">
      <Navbar />

      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-stone-900 mb-2">Investment Portal</h1>
          <p className="text-stone-600">Grow your savings with competitive returns</p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 mb-8">
          <Card data-testid="total-invested-card" className="border-stone-200">
            <CardHeader>
              <CardTitle className="text-sm font-medium text-stone-600">Total Invested</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div className="text-3xl font-bold text-stone-900">
                  ${totalInvested.toFixed(2)}
                </div>
                <div className="w-12 h-12 bg-indigo-50 rounded-xl flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-indigo-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="total-earnings-card" className="border-stone-200">
            <CardHeader>
              <CardTitle className="text-sm font-medium text-stone-600">Total Earnings</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div className="text-3xl font-bold text-amber-500">
                  ${totalEarnings.toFixed(2)}
                </div>
                <div className="w-12 h-12 bg-amber-50 rounded-xl flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-amber-500" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid lg:grid-cols-3 gap-6 mb-8">
          <Card className="border-stone-200 hover:shadow-lg transition-all">
            <CardHeader>
              <CardTitle>Conservative</CardTitle>
              <CardDescription>Low risk, steady returns</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-stone-900 mb-2">3.5%</div>
              <p className="text-sm text-stone-600 mb-4">Annual interest rate</p>
              <Button
                data-testid="invest-conservative-button"
                onClick={() => toast.info('Investment feature coming soon!')}
                variant="outline"
                className="w-full rounded-xl"
              >
                <Plus className="mr-2 w-4 h-4" />
                Invest
              </Button>
            </CardContent>
          </Card>

          <Card className="border-indigo-200 bg-indigo-50 hover:shadow-lg transition-all">
            <CardHeader>
              <CardTitle>Balanced</CardTitle>
              <CardDescription>Moderate risk, good returns</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-indigo-600 mb-2">5.0%</div>
              <p className="text-sm text-stone-600 mb-4">Annual interest rate</p>
              <Button
                data-testid="invest-balanced-button"
                onClick={() => toast.info('Investment feature coming soon!')}
                className="w-full rounded-xl bg-indigo-600 hover:bg-indigo-700"
              >
                <Plus className="mr-2 w-4 h-4" />
                Invest
              </Button>
            </CardContent>
          </Card>

          <Card className="border-stone-200 hover:shadow-lg transition-all">
            <CardHeader>
              <CardTitle>Growth</CardTitle>
              <CardDescription>Higher risk, maximum returns</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-stone-900 mb-2">7.5%</div>
              <p className="text-sm text-stone-600 mb-4">Annual interest rate</p>
              <Button
                data-testid="invest-growth-button"
                onClick={() => toast.info('Investment feature coming soon!')}
                variant="outline"
                className="w-full rounded-xl"
              >
                <Plus className="mr-2 w-4 h-4" />
                Invest
              </Button>
            </CardContent>
          </Card>
        </div>

        {investments.length > 0 && (
          <Card className="border-stone-200">
            <CardHeader>
              <CardTitle>Your Investments</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {investments.map((investment) => (
                  <div
                    key={investment.investment_id}
                    data-testid={`investment-${investment.investment_id}`}
                    className="flex items-center justify-between p-4 border border-stone-200 rounded-xl"
                  >
                    <div>
                      <div className="font-semibold text-stone-900">
                        ${investment.amount.toFixed(2)}
                      </div>
                      <div className="text-sm text-stone-600">
                        {investment.interest_rate}% APY
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold text-amber-500">
                        +${investment.earnings.toFixed(2)}
                      </div>
                      <div className="text-xs text-stone-500">Earned</div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        <Card className="border-stone-200 mt-6">
          <CardHeader>
            <CardTitle>How It Works</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-start">
                <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                  <span className="text-indigo-600 font-bold">1</span>
                </div>
                <div>
                  <h4 className="font-semibold text-stone-900 mb-1">Choose Your Plan</h4>
                  <p className="text-sm text-stone-600">
                    Select an investment plan based on your risk tolerance and financial goals.
                  </p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                  <span className="text-indigo-600 font-bold">2</span>
                </div>
                <div>
                  <h4 className="font-semibold text-stone-900 mb-1">Start Earning</h4>
                  <p className="text-sm text-stone-600">
                    Your investment starts earning interest immediately, calculated daily.
                  </p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                  <span className="text-indigo-600 font-bold">3</span>
                </div>
                <div>
                  <h4 className="font-semibold text-stone-900 mb-1">Watch It Grow</h4>
                  <p className="text-sm text-stone-600">
                    Track your earnings in real-time and withdraw anytime without penalties.
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}